import { Portal, Tooltip as ChakraTooltip } from "@chakra-ui/react";
import type { ComponentProps } from "react";
import type { TooltipSharedProps } from "./Tooltip.props";

type ChakraTooltipRootProps = ComponentProps<typeof ChakraTooltip.Root>;
type ChakraTooltipContentProps = ComponentProps<typeof ChakraTooltip.Content>;

export type TooltipProps = TooltipSharedProps &
  Omit<ChakraTooltipRootProps, "children"> & {
    // Backward-compatible aliases
    open?: boolean;
    disabled?: boolean;
    defaultOpen?: boolean;

    // Optional content props
    contentProps?: ChakraTooltipContentProps;

    /**
     * Wraps children in a span element for components that don't forward refs.
     * Use this when the child is an Icon or other component that doesn't support refs.
     */
    shouldWrapChildren?: boolean;
  };

const Tooltip = (props: TooltipProps): JSX.Element => {
  const {
    children,
    label,
    width,
    placement,
    hasArrow,
    showArrow,
    isOpen,
    open,
    isDisabled,
    disabled,
    defaultIsOpen,
    defaultOpen,
    closeDelay,
    openDelay,
    closeOnClick,
    bgColor,
    color,
    usePortal = true,
    contentProps,
    onOpen,
    onClose,
    shouldWrapChildren: _shouldWrapChildren, // Always wrap now, kept for API compat
    "data-testid": dataTestId,
    ...rest
  } = props;

  const openValue = open !== undefined ? open : isOpen;
  const disabledValue = disabled !== undefined ? disabled : isDisabled;
  const defaultOpenValue =
    defaultOpen !== undefined ? defaultOpen : defaultIsOpen;
  const showArrowValue = showArrow !== undefined ? showArrow : hasArrow;

  if (disabledValue || !label) {
    return <>{children}</>;
  }

  return (
    <ChakraTooltip.Root
      open={openValue}
      defaultOpen={defaultOpenValue}
      openDelay={openDelay}
      closeDelay={closeDelay}
      closeOnClick={closeOnClick}
      positioning={placement ? { placement } : undefined}
      onOpenChange={({ open: nextOpen }: { open: boolean }) => {
        if (nextOpen === true) {
          onOpen?.();
        } else {
          onClose?.();
        }
      }}
      {...rest}
    >
      <ChakraTooltip.Trigger asChild>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            verticalAlign: "middle",
          }}
        >
          {children}
        </span>
      </ChakraTooltip.Trigger>
      <Portal disabled={!usePortal}>
        <ChakraTooltip.Positioner>
          <ChakraTooltip.Content
            width={width}
            ref={undefined}
            data-testid={dataTestId}
            {...contentProps}
            css={{
              ...(bgColor ? { "--tooltip-bg": bgColor } : {}),
              ...(contentProps?.css ?? {}),
            }}
            color={color}
          >
            {showArrowValue ? (
              <ChakraTooltip.Arrow>
                <ChakraTooltip.ArrowTip />
              </ChakraTooltip.Arrow>
            ) : null}
            {label}
          </ChakraTooltip.Content>
        </ChakraTooltip.Positioner>
      </Portal>
    </ChakraTooltip.Root>
  );
};

export default Tooltip;
